import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { buildLoginUrl, getCurrentUser, logout as logoutRequest, type CurrentUser } from '@/api/auth';

export const useAuthStore = defineStore('auth', () => {
    const user = ref<CurrentUser | null>(null);
    const ready = ref(false);

    const isAuthenticated = computed(() => user.value !== null);
    const role = computed(() => user.value?.role ?? null);
    const canAdmin = computed(() => user.value?.roles.includes('admin') ?? false);
    const canMutate = computed(() => canAdmin.value || (user.value?.roles.includes('operator') ?? false));

    async function bootstrap(): Promise<void> {
        user.value = await tryGetCurrentUser();
        ready.value = true;
    }

    async function tryGetCurrentUser(): Promise<CurrentUser | null> {
        try {
            return await getCurrentUser();
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                return null;
            }
            throw error;
        }
    }

    function login(returnUrl = '/'): void {
        window.location.assign(buildLoginUrl(returnUrl));
    }

    async function logout(): Promise<void> {
        try {
            const endSessionUrl = await logoutRequest(true);
            user.value = null;
            if (endSessionUrl) {
                window.location.assign(endSessionUrl);
                return;
            }
        } finally {
            user.value = null;
        }
    }

    async function refresh(): Promise<CurrentUser | null> {
        user.value = await tryGetCurrentUser();
        return user.value;
    }

    return {
        user,
        ready,
        isAuthenticated,
        role,
        canAdmin,
        canMutate,
        bootstrap,
        login,
        logout,
        refresh
    };
});
